/**
 * Vinami: kleine interacties bovenop Horizon.
 * - vn-pairing-game: het wijnspel met de hendel (homepage)
 * - vn-box-qty: aantal dozen op de productpagina, werkt de knoptekst bij
 * - [data-vn-nav]: selects in de filterbalk die naar een URL navigeren
 * - [data-vn-param]: select die één URL-parameter zet (sortering)
 */

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class VnPairingGame extends HTMLElement {
  connectedCallback() {
    this.reel = this.querySelector('.vn-game__window img');
    this.dishes = [...this.querySelectorAll('.vn-game__dish')];
    this.results = [...this.querySelectorAll('[data-result]')];
    this.pool = this.results.map((r) => r.dataset.img).filter(Boolean);
    this.idle = this.dataset.idle;
    this.spinning = false;

    this.dishes.forEach((d) => d.addEventListener('click', () => this.pick(d)));
    this.querySelectorAll('[data-pull]').forEach((b) => b.addEventListener('click', () => this.pull()));
  }

  get active() {
    return this.dishes.find((d) => d.getAttribute('aria-checked') === 'true') || this.dishes[0];
  }

  pick(dish) {
    if (this.spinning) return;
    this.dishes.forEach((d) => d.setAttribute('aria-checked', String(d === dish)));
    this.querySelector('[data-reel-dish]').textContent = dish.dataset.label;
    this.querySelector('[data-reel-label]').textContent = 'Klaar?';
    this.querySelector('[data-pull-label]').textContent = 'Trek aan de hendel';
    this.results.forEach((r) => (r.hidden = true));
    if (this.idle) this.reel.src = this.idle;
  }

  pull() {
    const dish = this.active;
    if (this.spinning || !dish) return;
    let ids = (dish.dataset.ids || '').split(',').filter(Boolean);
    // Geen wijnen gekoppeld aan dit gerecht: kies uit alle wijnen van het spel
    if (!ids.length) ids = this.results.map((r) => r.dataset.result);
    if (!ids.length) return;
    const finalId = ids[Math.floor(Math.random() * ids.length)];
    const final = this.results.find((r) => r.dataset.result === finalId);

    this.spinning = true;
    this.results.forEach((r) => (r.hidden = true));
    this.setAttribute('data-spinning', '');
    this.setAttribute('data-lever', '');
    setTimeout(() => this.removeAttribute('data-lever'), 300);
    this.querySelector('[data-reel-label]').textContent = 'Even proeven…';
    this.querySelector('[data-pull-label]').textContent = 'Even geduld…';
    this.querySelectorAll('[data-pull]').forEach((b) => b.setAttribute('aria-disabled', 'true'));

    const pool = [...this.pool].sort(() => Math.random() - 0.5);
    const delays = reducedMotion() ? [] : [50, 50, 55, 60, 60, 70, 80, 95, 115, 140, 175, 220, 280];
    let i = 0;

    const done = () => {
      this.spinning = false;
      this.removeAttribute('data-spinning');
      this.reel.style.transform = 'none';
      if (final) {
        if (final.dataset.img) this.reel.src = final.dataset.img;
        final.querySelector('[data-why-target]').textContent = dish.dataset.why || '';
        final.hidden = false;
      }
      this.querySelector('[data-reel-label]').textContent = 'Onze keuze';
      this.querySelector('[data-pull-label]').textContent = 'Nog een keer';
      this.querySelectorAll('[data-pull]').forEach((b) => b.removeAttribute('aria-disabled'));
    };

    const step = () => {
      if (i >= delays.length) return done();
      if (pool.length) this.reel.src = pool[i % pool.length];
      this.reel.style.transform = i % 2 ? 'translateY(-3%)' : 'translateY(3%)';
      setTimeout(step, delays[i++]);
    };
    setTimeout(step, 120);
  }
}
if (!customElements.get('vn-pairing-game')) customElements.define('vn-pairing-game', VnPairingGame);

/* Vinami: bedrag met klein euroteken; hele bedragen zonder ,00 (zelfde opmaak als snippets/vn-money.liquid) */
function vnMoney(cents) {
  const total = Math.round(Number(cents || 0));
  const [a, b] = (total / 100).toFixed(2).split('.');
  const dots = a.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `<span class="vn-money"><span class="vn-money__cur">€</span>${total % 100 === 0 ? dots : `${dots},${b}`}</span>`;
}

class VnBoxQty extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[name="quantity"]');
    this.out = this.querySelector('[data-qty]');
    this.label = this.closest('form')?.querySelector('[data-cta]');
    this.boxPrice = Number(this.dataset.price || 0);
    this.step = Math.max(1, Number(this.dataset.step || 1));
    this.boxes = 1;
    this.format = this.dataset.format || '€ {{amount_with_comma_separator}}';
    this.querySelector('[data-dec]')?.addEventListener('click', () => this.set(this.value - 1));
    this.querySelector('[data-inc]')?.addEventListener('click', () => this.set(this.value + 1));
  }

  /** aantal dozen */
  get value() {
    return this.boxes;
  }

  /** aantal stuks voor de winkelmand (flessen of dozen, zie data-step) */
  getValue() {
    return this.boxes * this.step;
  }

  money(cents) {
    return vnMoney(cents);
  }

  set(n) {
    const v = Math.max(1, Math.min(99, n));
    this.boxes = v;
    this.input.value = String(v * this.step);
    this.out.textContent = String(v);
    if (this.label) {
      const unit = v === 1 ? 'Doos' : `${v} dozen`;
      this.label.innerHTML = `${unit} in winkelmand${this.boxPrice ? ' · ' + this.money(this.boxPrice * v) : ''}`;
    }
  }
}
if (!customElements.get('vn-box-qty')) customElements.define('vn-box-qty', VnBoxQty);

document.addEventListener('change', (e) => {
  const el = e.target;
  if (!(el instanceof HTMLSelectElement)) return;
  if (el.hasAttribute('data-vn-nav') && el.value) {
    window.location.href = el.value;
  } else if (el.dataset.vnParam) {
    const url = new URL(window.location.href);
    url.searchParams.set(el.dataset.vnParam, el.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  }
});

/**
 * Collectiefilter in de browser (vn-main-collection zonder Search & Discovery).
 * Smaak, kleur, land, prijs per fles, soort, biologisch en sortering; leest ook
 * filter.p.m.custom.style / wine_type / country / soort uit de URL (links vanaf de homepage).
 */
class VnCollFilter extends HTMLElement {
  connectedCallback() {
    if (!this.hasAttribute('data-client')) return;
    this.grid = this.querySelector('.vn-coll__grid');
    if (!this.grid) return;
    this.items = [...this.grid.querySelectorAll('.vn-coll__item')];
    this.state = { style: '', colour: '', country: '', soort: '', price: '', bio: false, sort: '', q: '' };
    const q = new URLSearchParams(location.search);
    this.state.q = q.get('q') || '';
    /* Zoeken: direct filteren tijdens het typen, niet naar de zoekpagina */
    this.search = this.querySelector('[data-search]');
    const norm = (t) => (t || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    this.norm = norm;
    this.items.forEach((it) => { it.dataset.searchNorm = norm(it.dataset.search || it.dataset.title); });
    if (this.search) {
      this.search.value = this.state.q;
      this.search.addEventListener('input', () => { this.state.q = this.search.value; this.apply(true); });
      const form = this.querySelector('[data-search-form]');
      if (form) form.addEventListener('submit', (e) => { e.preventDefault(); this.search.blur(); });
      const clr = this.querySelector('[data-search-clear]');
      if (clr) clr.addEventListener('click', () => { this.state.q = ''; this.search.value = ''; this.apply(true); this.search.focus(); });
    }
    this.state.style = q.get('filter.p.m.custom.style') || '';
    this.state.colour = q.get('filter.p.m.custom.wine_type') || '';
    this.state.country = q.get('filter.p.m.custom.country') || '';
    this.state.soort = q.get('filter.p.m.custom.soort') || '';
    this.state.bio = q.get('filter.p.m.custom.bio') === '1' || q.get('filter.p.m.custom.bio') === 'true';

    this.addEventListener('click', (e) => {
      const btn = e.target.closest('button');
      if (!btn || !this.contains(btn)) return;
      const group = btn.closest('[data-group]');
      if (group) {
        this.state[group.dataset.group] = btn.dataset.value || '';
      } else if (btn.hasAttribute('data-bio')) {
        this.state.bio = !this.state.bio;
      } else if (btn.hasAttribute('data-clear')) {
        Object.assign(this.state, { style: '', colour: '', country: '', soort: '', price: '', bio: false, q: '' });
        if (this.search) this.search.value = '';
      } else return;
      this.apply(true);
    });
    this.addEventListener('change', (e) => {
      const el = e.target;
      if (!(el instanceof HTMLSelectElement)) return;
      if (el.dataset.filter) this.state[el.dataset.filter] = el.value;
      else if (el.hasAttribute('data-sort')) this.state.sort = el.value;
      else return;
      e.stopPropagation();
      this.apply(true);
    });
    this.apply(false);
  }

  apply(updateUrl) {
    const s = this.state;
    let shown = 0;
    const [pmin, pmax] = s.price ? s.price.split('-').map(Number) : [0, Infinity];
    const words = this.norm ? this.norm(s.q).split(/\s+/).filter(Boolean) : [];
    for (const it of this.items) {
      const d = it.dataset;
      const price = Number(d.price || 0);
      const ok =
        (!s.style || d.style === s.style) &&
        (!s.colour || d.colour === s.colour) &&
        (!s.country || d.country === s.country) &&
        (!s.soort || d.soort === s.soort) &&
        (!s.bio || d.bio === '1') &&
        (!s.price || (price >= pmin && price <= pmax)) &&
        words.every((w) => (d.searchNorm || '').includes(w));
      it.hidden = !ok;
      if (ok) shown++;
    }
    const sorted = [...this.items];
    if (s.sort === 'price-asc') sorted.sort((a, b) => a.dataset.price - b.dataset.price);
    else if (s.sort === 'price-desc') sorted.sort((a, b) => b.dataset.price - a.dataset.price);
    else if (s.sort === 'title') sorted.sort((a, b) => a.dataset.title.localeCompare(b.dataset.title, 'nl'));
    else {
      /* Aanbevolen: eerst de toppers (custom.rank), de rest in een volgorde die elke dag wisselt */
      const day = new Date().toISOString().slice(0, 10);
      const hash = (str) => {
        let h = 2166136261;
        for (let i = 0; i < str.length; i += 1) h = Math.imul(h ^ str.charCodeAt(i), 16777619);
        return h >>> 0;
      };
      sorted.sort((a, b) => (a.dataset.rank - b.dataset.rank) || (hash(day + a.dataset.title) - hash(day + b.dataset.title)));
    }
    sorted.forEach((it) => this.grid.append(it));

    this.querySelectorAll('[data-group]').forEach((g) => {
      g.querySelectorAll('button').forEach((b) => b.setAttribute('aria-current', String((b.dataset.value || '') === s[g.dataset.group])));
    });
    this.querySelectorAll('select[data-filter]').forEach((sel) => { sel.value = s[sel.dataset.filter]; });
    const sortSel = this.querySelector('select[data-sort]');
    if (sortSel) sortSel.value = s.sort;
    const bio = this.querySelector('[data-bio]');
    if (bio) bio.setAttribute('aria-checked', String(s.bio));
    const any = s.style || s.colour || s.country || s.soort || s.price || s.bio || s.q.trim();
    const clr = this.querySelector('[data-search-clear]');
    if (clr) clr.hidden = !s.q;
    const et = this.querySelector('[data-empty-title]');
    if (et) et.textContent = s.q.trim() ? 'Niks gevonden voor \u2018' + s.q.trim() + '\u2019.' : 'Niks gevonden met deze combinatie.';
    this.querySelectorAll('[data-clear]').forEach((c) => { if (!c.closest('[data-empty]')) c.hidden = !any; });
    const empty = this.querySelector('[data-empty]');
    if (empty) empty.hidden = shown > 0;
    const pad = (n) => (n < 10 ? '0' + n : String(n));
    const count = this.querySelector('[data-count]');
    if (count) count.textContent = pad(shown);
    const sh = this.querySelector('[data-shown]');
    if (sh) sh.textContent = String(shown);

    if (updateUrl) {
      const url = new URL(location.href);
      const map = { style: 'filter.p.m.custom.style', colour: 'filter.p.m.custom.wine_type', country: 'filter.p.m.custom.country', soort: 'filter.p.m.custom.soort' };
      for (const [k, p] of Object.entries(map)) { if (s[k]) url.searchParams.set(p, s[k]); else url.searchParams.delete(p); }
      if (s.bio) url.searchParams.set('filter.p.m.custom.bio', '1'); else url.searchParams.delete('filter.p.m.custom.bio');
      if (s.q.trim()) url.searchParams.set('q', s.q.trim()); else url.searchParams.delete('q');
      history.replaceState(null, '', url);
    }
  }
}
if (!customElements.get('vn-coll-filter')) customElements.define('vn-coll-filter', VnCollFilter);

/**
 * Flessen even groot: productfoto's hebben verschillend veel witruimte. Meet per foto (kleine
 * CORS-kopie van het Shopify-CDN) waar de fles staat en schaal zodat elke fles ongeveer
 * 90% van de hoogte van de kaart vult. Lukt meten niet, dan blijft de foto zoals hij is.
 */
const vnFit = (() => {
  const cache = new Map();
  const measure = (src) => {
    if (cache.has(src)) return cache.get(src);
    const pr = new Promise((resolve) => {
      let u;
      try { u = new URL(src, location.href); } catch (e) { return resolve(null); }
      u.searchParams.set('width', '160');
      const im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = () => {
        try {
          const w = im.naturalWidth, h = im.naturalHeight;
          const c = document.createElement('canvas');
          c.width = w; c.height = h;
          const ctx = c.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(im, 0, 0);
          const d = ctx.getImageData(0, 0, w, h).data;
          const isInk = (i) => d[i + 3] > 24 && (d[i] < 238 || d[i + 1] < 238 || d[i + 2] < 238);
          const rows = new Array(h).fill(0), cols = new Array(w).fill(0);
          for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) if (isInk((y * w + x) * 4)) { rows[y]++; cols[x]++; }
          const minR = Math.max(1, w * 0.01), minC = Math.max(1, h * 0.01);
          let y0 = rows.findIndex((n) => n >= minR), y1 = h - 1 - [...rows].reverse().findIndex((n) => n >= minR);
          let x0 = cols.findIndex((n) => n >= minC), x1 = w - 1 - [...cols].reverse().findIndex((n) => n >= minC);
          if (y0 < 0 || x0 < 0 || y1 <= y0 || x1 <= x0) return resolve(null);
          resolve({ x0: x0 / w, x1: (x1 + 1) / w, y0: y0 / h, y1: (y1 + 1) / h, ar: w / h });
        } catch (e) { resolve(null); }
      };
      im.onerror = () => resolve(null);
      im.src = u.toString();
    });
    cache.set(src, pr);
    return pr;
  };
  const place = (img, box) => {
    const el = img.parentElement;
    const W = el.clientWidth, H = el.clientHeight;
    if (!W || !H) return;
    // Beeld beslaat de hele media (padding 0 in fit-modus), object-fit: contain
    const s0 = Math.min(W / box.ar, H);
    const iw = s0 * box.ar, ih = s0;
    const ox = (W - iw) / 2, oy = (H - ih) / 2;
    const bw = (box.x1 - box.x0) * iw, bh = (box.y1 - box.y0) * ih;
    const bcx = ox + ((box.x0 + box.x1) / 2) * iw, bcy = oy + ((box.y0 + box.y1) / 2) * ih;
    const k = Math.max(0.6, Math.min(3, Math.min((H * 0.88) / bh, (W * 0.82) / bw)));
    img.style.transformOrigin = `${bcx}px ${bcy}px`;
    img.style.setProperty('--vn-fit', `translate(${W / 2 - bcx}px, ${H / 2 - bcy}px) scale(${k})`);
    img.classList.add('is-fit');
  };
  const ro = 'ResizeObserver' in window ? new ResizeObserver((es) => es.forEach((e) => { const img = e.target.querySelector('img'); if (img && img._vnBox) place(img, img._vnBox); })) : null;
  const run = (root) => {
    root.querySelectorAll('.vn-card__media img:not([data-vn-fit])').forEach((img) => {
      img.setAttribute('data-vn-fit', '');
      const go = () => measure(img.currentSrc || img.src).then((box) => {
        if (!box) return;
        img._vnBox = box;
        place(img, box);
        if (ro) ro.observe(img.parentElement);
      });
      if (img.complete && img.naturalWidth) go(); else img.addEventListener('load', go, { once: true });
    });
  };
  return run;
})();
const vnFitAll = () => vnFit(document);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', vnFitAll); else vnFitAll();
let vnFitQueued = false;
new MutationObserver(() => {
  if (vnFitQueued) return;
  vnFitQueued = true;
  requestAnimationFrame(() => { vnFitQueued = false; vnFitAll(); });
}).observe(document.documentElement, { childList: true, subtree: true });

/* Animaties op de productpagina: pas starten als ze in beeld komen. Bij "minder beweging" blijft alles stil. */
(() => {
  const init = (root) => {
    if (reducedMotion() || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { threshold: 0.35 });
    root.querySelectorAll('.vn-scene:not(.is-ready)').forEach((el) => {
      el.classList.add('is-ready');
      io.observe(el);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();

/* Zakelijk-uitklapmenu: de kaart direct onder het menu-item zetten, binnen het scherm.
   CSS kan dat niet, omdat Horizon het menu-item niet als ankerpunt laat gebruiken. */
(() => {
  const place = () => {
    document.querySelectorAll('.menu-list__list-item.vn-has-card:not([slot="overflow"])').forEach((li) => {
      const sub = li.querySelector(':scope > .menu-list__submenu');
      const card = sub && sub.querySelector('.vn-menu-card');
      if (!card) return;
      const lr = li.getBoundingClientRect();
      const sr = sub.getBoundingClientRect();
      const w = card.offsetWidth;
      const vw = document.documentElement.clientWidth;
      let x = lr.left - sr.left - 12;
      x = Math.min(x, vw - 16 - w - sr.left);
      x = Math.max(x, 16 - sr.left);
      card.style.insetInlineEnd = 'auto';
      card.style.left = `${Math.round(x)}px`;
    });
  };
  const init = () => {
    place();
    document.querySelectorAll('.menu-list__list-item.vn-has-card').forEach((li) => {
      li.addEventListener('pointerenter', place);
      li.addEventListener('focusin', place);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  window.addEventListener('resize', place);
  document.addEventListener('shopify:section:load', init);
})();

/* Mini-winkelmand: bevestiging na toevoegen en één passende wijn.
   Horizon meldt elke wijziging met 'shopify:cart:lines-update' en tekent de lade daarna opnieuw. */
(() => {
  const suggestCache = new Map();
  let lastAdded = '';

  const drawer = () => document.getElementById('cart-drawer');

  const showAdded = () => {
    const el = drawer()?.querySelector('.vn-cart-added');
    if (!el || !lastAdded) return;
    el.textContent = lastAdded === '-' ? 'Toegevoegd aan je winkelmand' : `Toegevoegd: ${lastAdded}`;
    el.hidden = false;
    clearTimeout(showAdded.t);
    showAdded.t = setTimeout(() => { el.hidden = true; lastAdded = ''; }, 5000);
  };

  const loadSuggest = async () => {
    const slot = drawer()?.querySelector('.vn-cart-suggest');
    const id = slot?.dataset.productId;
    if (!slot || !id) return;
    const key = `${id}|${slot.dataset.inCart}`;
    if (!suggestCache.has(key)) {
      const get = async (intent) => {
        const r = await fetch(`${window.Shopify?.routes?.root || '/'}recommendations/products?section_id=vn-cart-suggest&product_id=${id}&limit=6&intent=${intent}`);
        if (!r.ok) return '';
        const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
        return doc.querySelector('.vn-sug')?.outerHTML || '';
      };
      try {
        suggestCache.set(key, (await get('complementary')) || (await get('related')));
      } catch (_) {
        suggestCache.set(key, '');
      }
    }
    const html = suggestCache.get(key);
    const current = drawer()?.querySelector('.vn-cart-suggest');
    if (!current) return;
    current.innerHTML = html;
    current.hidden = !html;
  };

  const refresh = () => requestAnimationFrame(() => { showAdded(); loadSuggest(); });

  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (event.action === 'add') {
      const form = event.target instanceof Element ? event.target.closest('[data-vn-title]') : null;
      lastAdded = form?.dataset.vnTitle || '-';
    }
    (event.promise || Promise.resolve()).then(() => setTimeout(refresh, 60)).catch(() => {});
  });
  document.addEventListener('theme-drawer:open', refresh, true);


  if (document.readyState !== 'loading') loadSuggest();
  else document.addEventListener('DOMContentLoaded', loadSuggest);
})();

/* Mini-winkelmand
   1. Kaartje onder het winkelmandicoon na toevoegen (telefoon en pc): "Toegevoegd" plus de hele winkelmand.
   2. Op pc: hetzelfde overzicht zodra je met de muis over het icoon gaat, direct uit het geheugen.
   3. Op telefoon: vaste balk onderaan met dozen, totaal en Afrekenen.
   Per regel dozen erbij, eraf of verwijderen via /cart/change.js; daarna een 'shopify:cart:lines-update'
   zodat Horizon het bolletje en de lade zelf bijwerkt. Klik op het icoon opent de volledige lade. */
(() => {
  const cfg = window.vnConfig || {};
  const per = Number(cfg.per) || 1;
  const root = cfg.root && cfg.root !== '/' ? `${cfg.root}/` : '/';
  const checkoutUrl = `${root}checkout`;
  const cartUrl = cfg.cartUrl || '/cart';
  const fineHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  let cart = null;
  let hideTimer = 0;
  let mode = '';
  let addedKey = '';
  let busy = 0;

  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const money = vnMoney;
  const boxes = (q) => Math.ceil(Number(q || 0) / per);
  const boxLabel = (n) => `${n} ${n === 1 ? 'doos' : 'dozen'}`;
  const img = (url, w) => (url ? `${url}${url.includes('?') ? '&' : '?'}width=${w}` : '');

  const fetchCart = async () => {
    try {
      const r = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (r.ok) cart = await r.json();
    } catch (_) { /* geen verbinding: laat de vorige stand staan */ }
    return cart;
  };

  const anchor = () => document.querySelector('header cart-icon, cart-icon');
  const openDrawer = () => {
    const d = document.getElementById('cart-drawer');
    if (d && typeof d.open === 'function') d.open();
    else window.location.href = cartUrl;
  };

  /* ---------- kaartje / overzicht ---------- */
  const panel = document.createElement('div');
  panel.className = 'vn-mini';
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', 'Winkelmand');
  panel.hidden = true;

  const line = (it) => {
    const n = boxes(it.quantity);
    return `
    <li class="vn-mini__line${it.key === addedKey ? ' is-new' : ''}" data-key="${esc(it.key)}">
      <a class="vn-mini__img" href="${esc(it.url || '#')}" tabindex="-1">${it.image ? `<img src="${esc(img(it.image, 160))}" alt="" loading="lazy">` : ''}</a>
      <span class="vn-mini__txt">
        <a class="vn-mini__name" href="${esc(it.url || '#')}">${esc(it.product_title)}</a>
        <span class="vn-mini__meta">${money(it.final_line_price ?? it.line_price)}</span>
        <span class="vn-mini__qty">
          <button type="button" class="vn-mini__step" data-step="-1" aria-label="Doos minder"${n <= 1 ? ' disabled' : ''}>−</button>
          <span class="vn-mini__n" aria-live="polite">${boxLabel(n)}</span>
          <button type="button" class="vn-mini__step" data-step="1" aria-label="Doos erbij">+</button>
          <button type="button" class="vn-mini__del" data-remove>Verwijder</button>
        </span>
      </span>
    </li>`;
  };

  const render = () => {
    const items = cart?.items || [];
    const total = boxes(cart?.item_count);
    const added = mode === 'added' ? items.find((i) => i.key === addedKey) : null;
    let body = '';
    if (added) body += `<p class="vn-mini__ok">Toegevoegd: ${esc(added.product_title)}</p>`;
    else body += `<p class="vn-mini__title">Je winkelmand${items.length ? `<span>${boxLabel(total)}</span>` : ''}</p>`;
    if (!items.length) {
      body += `<p class="vn-mini__empty">Nog leeg. Tijd voor een doos.</p>`;
      body += `<a class="vn-btn vn-btn--secondary vn-btn--full" href="${esc(root)}collections/all">Bekijk de wijnen</a>`;
      return body;
    }
    const sorted = added ? [added, ...items.filter((i) => i !== added)] : items;
    body += `<ul class="vn-mini__lines">${sorted.map(line).join('')}</ul>`;
    body += `<p class="vn-mini__sum"><span>Totaal, ${boxLabel(total)}</span><span>${money(cart?.total_price)}</span></p>`;
    body += `<div class="vn-mini__actions">
        <a class="vn-btn vn-btn--full" href="${esc(checkoutUrl)}">Afrekenen</a>
        <button type="button" class="vn-btn vn-btn--secondary vn-btn--full" data-vn-open-cart>Bekijk winkelmand</button>
      </div>`;
    return body;
  };

  const paint = () => {
    panel.innerHTML = `<button type="button" class="vn-mini__close" aria-label="Sluiten" data-vn-close>×</button>${render()}`;
  };

  const place = () => {
    const a = anchor();
    const vw = document.documentElement.clientWidth;
    if (!a) { panel.style.top = '12px'; panel.style.right = '12px'; return; }
    const r = a.getBoundingClientRect();
    panel.style.top = `${Math.max(8, r.bottom + 10)}px`;
    panel.style.maxHeight = `calc(100vh - ${Math.max(8, r.bottom + 10) + 16}px)`;
    if (vw < 750) {
      panel.style.left = '12px';
      panel.style.right = '12px';
    } else {
      panel.style.left = 'auto';
      panel.style.right = `${Math.max(16, vw - r.right - 6)}px`;
    }
  };

  const show = (m) => {
    mode = m;
    clearTimeout(hideTimer);
    paint();
    place();
    panel.hidden = false;
    requestAnimationFrame(() => panel.classList.add('is-open'));
    if (m === 'added') hideTimer = setTimeout(hide, 7000);
  };
  const hide = () => {
    if (busy) return;
    clearTimeout(hideTimer);
    panel.classList.remove('is-open');
    mode = '';
    addedKey = '';
    setTimeout(() => { if (!panel.classList.contains('is-open')) panel.hidden = true; }, 160);
  };

  /* Aantal wijzigen: meteen in beeld, daarna Shopify, daarna Horizon laten bijwerken */
  const change = async (key, bottles) => {
    const it = cart?.items.find((i) => i.key === key);
    if (!it) return;
    busy += 1;
    panel.classList.add('is-busy');
    const unit = (it.final_line_price ?? it.line_price) / Math.max(1, it.quantity);
    if (bottles > 0) {
      cart.total_price += unit * (bottles - it.quantity);
      cart.item_count += bottles - it.quantity;
      Object.assign(it, { quantity: bottles, final_line_price: unit * bottles, line_price: unit * bottles });
    } else {
      cart.total_price -= unit * it.quantity;
      cart.item_count -= it.quantity;
      cart.items = cart.items.filter((i) => i !== it);
    }
    paint();
    renderBar();
    try {
      const r = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: bottles }),
      });
      if (r.ok) cart = await r.json();
      else await fetchCart();
    } catch (_) {
      await fetchCart();
    }
    busy -= 1;
    panel.classList.toggle('is-busy', busy > 0);
    paint();
    renderBar();
    const ev = new Event('shopify:cart:lines-update', { bubbles: true });
    Object.assign(ev, {
      action: 'update',
      lines: [{ merchandiseId: String(it.variant_id), quantity: bottles }],
      promise: Promise.resolve({ cart: { totalQuantity: cart?.item_count ?? 0 }, detail: {} }),
      vnMini: true,
    });
    panel.dispatchEvent(ev);
  };

  panel.addEventListener('click', (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    clearTimeout(hideTimer);
    if (t.closest('[data-vn-close]')) { busy = 0; hide(); return; }
    if (t.closest('[data-vn-open-cart]')) { hide(); openDrawer(); return; }
    const row = t.closest('[data-key]');
    if (!row) return;
    const it = cart?.items.find((i) => i.key === row.dataset.key);
    if (!it) return;
    const step = t.closest('[data-step]');
    if (step) change(it.key, Math.max(per, it.quantity + Number(step.dataset.step) * per));
    if (t.closest('[data-remove]')) change(it.key, 0);
  });
  panel.addEventListener('pointerenter', () => clearTimeout(hideTimer));
  panel.addEventListener('pointerleave', () => { hideTimer = setTimeout(hide, mode === 'added' ? 2500 : 200); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !panel.hidden) { busy = 0; hide(); } });
  document.addEventListener('pointerdown', (e) => {
    if (panel.hidden || panel.contains(e.target) || anchor()?.contains(e.target)) return;
    hide();
  });
  window.addEventListener('resize', () => { if (!panel.hidden) place(); });
  window.addEventListener('scroll', () => { if (!panel.hidden) place(); }, { passive: true });

  /* ---------- balk onderaan (telefoon) ---------- */
  const bar = document.createElement('div');
  bar.className = 'vn-cartbar';
  bar.hidden = true;
  const renderBar = () => {
    const n = boxes(cart?.item_count);
    bar.hidden = !n || cfg.template === 'cart';
    document.documentElement.classList.toggle('vn-has-cartbar', !bar.hidden);
    if (bar.hidden) return;
    bar.innerHTML = `
      <button type="button" class="vn-cartbar__info" data-vn-open-cart>
        <span class="vn-cartbar__count">${boxLabel(n)}</span>
        <span class="vn-cartbar__total">${money(cart?.total_price)}</span>
      </button>
      <a class="vn-btn vn-btn--sm" href="${esc(checkoutUrl)}">Afrekenen</a>`;
  };
  bar.addEventListener('click', (e) => {
    if (e.target instanceof Element && e.target.closest('[data-vn-open-cart]')) openDrawer();
  });

  /* ---------- koppelingen ---------- */
  const hookAnchor = () => {
    const a = anchor();
    if (!a || a.dataset.vnMini) return;
    a.dataset.vnMini = '1';
    let enterTimer = 0;
    a.addEventListener('pointerenter', () => {
      if (!fineHover.matches || mode === 'added') return;
      clearTimeout(hideTimer);
      enterTimer = setTimeout(() => {
        show('hover');
        fetchCart().then(() => { if (mode === 'hover' && !busy) paint(); });
      }, 150);
    });
    a.addEventListener('pointerleave', () => {
      clearTimeout(enterTimer);
      if (mode === 'hover') hideTimer = setTimeout(hide, 200);
    });
    a.addEventListener('click', () => { busy = 0; hide(); });
  };

  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (event.vnMini) return;
    const first = event.lines?.[0];
    (event.promise || Promise.resolve())
      .then(async (res) => {
        if (res?.detail?.didError) return;
        await fetchCart();
        renderBar();
        if (event.action !== 'add' || !cart) {
          if (!panel.hidden && !busy) paint();
          return;
        }
        const id = String(first?.merchandiseId || '').split('/').pop();
        const it = cart.items.find((i) => String(i.variant_id) === id) || cart.items[0];
        if (!it) return;
        addedKey = it.key;
        show('added');
      })
      .catch(() => {});
  });
  document.addEventListener('theme-drawer:open', () => { busy = 0; hide(); }, true);
  /* Nooit tegelijk met een uitklapmenu (zoals Zakelijk): menu in beeld, winkelmandkaartje dicht */
  document.addEventListener('pointerover', (e) => {
    if (panel.hidden || !(e.target instanceof Element)) return;
    if (e.target.closest('.menu-list__list-item, .menu-list__submenu')) { busy = 0; hide(); }
  });

  const init = async () => {
    document.body.append(panel, bar);
    hookAnchor();
    await fetchCart();
    renderBar();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', hookAnchor);
})();

/* Vaste aankondigingsbalk: hoogte doorgeven, zodat de vaste navigatie er precies onder hangt. */
(() => {
  const bar = document.querySelector('.announcement-bar');
  if (!bar) return;
  const set = () => document.documentElement.style.setProperty('--vn-ann-h', `${bar.offsetHeight}px`);
  set();
  if ('ResizeObserver' in window) new ResizeObserver(set).observe(bar);
})();

/* Laadscherm tussen pagina's: alleen als de volgende pagina langer dan 0,3 seconde op zich laat wachten.
   Een glas dat zich vult, de stappen van druif tot glas en een laadbalk. */
(() => {
  const STEPS = ['Oogsten', 'Persen', 'Vergisten', 'Rijpen op vat', 'Inschenken'];
  let el = null;
  let showTimer = 0;
  let stepTimer = 0;
  let safety = 0;

  const build = () => {
    el = document.createElement('div');
    el.className = 'vn-loader';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.innerHTML = `
      <div class="vn-loader__card">
        <svg class="vn-loader__glass" viewBox="0 0 150 150" fill="none" stroke="#111" stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <clipPath id="vn-loader-bowl"><path d="M32 18 C29 62 44 84 60 84 C76 84 91 62 88 18 Z"/></clipPath>
          <g clip-path="url(#vn-loader-bowl)">
            <rect class="vn-loader__wine" x="20" y="18" width="80" height="70" fill="#910e12" stroke="none"/>
            <path class="vn-loader__wave" d="M20 0 q10 -5 20 0 t20 0 t20 0 t20 0 t20 0 v8 h-100 z" fill="#910e12" stroke="none"/>
          </g>
          <path d="M30 16 C27 62 43 86 60 86 C77 86 93 62 90 16"/>
          <path d="M60 86 L60 128"/><path d="M40 134 Q60 124 80 134"/>
          <g class="vn-loader__grapes" transform="translate(78 84) scale(.5)">
            <path d="M62 36 Q64 22 72 14"/>
            <path d="M68 28 C80 10 104 16 100 30 C90 38 78 36 68 28 Z" fill="#a8402c" stroke-width="7"/>
            <circle cx="44" cy="46" r="9" fill="#fff" stroke-width="7"/><circle cx="62" cy="46" r="9" fill="#fff" stroke-width="7"/><circle cx="80" cy="46" r="9" fill="#fff" stroke-width="7"/>
            <circle cx="53" cy="62" r="9" fill="#fff" stroke-width="7"/><circle cx="71" cy="62" r="9" fill="#a8402c" stroke-width="7"/><circle cx="62" cy="78" r="9" fill="#fff" stroke-width="7"/>
          </g>
        </svg>
        <p class="vn-loader__step">${STEPS[0]}</p>
        <ol class="vn-loader__dots" aria-hidden="true">${STEPS.map((_, i) => `<li${i === 0 ? ' class="is-on"' : ''}></li>`).join('')}</ol>
        <div class="vn-loader__bar" aria-hidden="true"><span></span></div>
      </div>`;
    document.body.append(el);
  };

  const setStep = (i) => {
    if (!el) return;
    el.querySelector('.vn-loader__step').textContent = STEPS[i];
    el.querySelectorAll('.vn-loader__dots li').forEach((li, n) => li.classList.toggle('is-on', n <= i));
  };

  const start = () => {
    if (!el) build();
    let i = 0;
    setStep(0);
    el.classList.remove('is-open');
    void el.offsetWidth;
    el.classList.add('is-open');
    clearInterval(stepTimer);
    stepTimer = setInterval(() => { i = Math.min(i + 1, STEPS.length - 1); setStep(i); }, 650);
    clearTimeout(safety);
    safety = setTimeout(stop, 12000);
  };

  const stop = () => {
    clearTimeout(showTimer);
    clearInterval(stepTimer);
    clearTimeout(safety);
    el?.classList.remove('is-open');
  };

  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = e.target instanceof Element ? e.target.closest('a[href]') : null;
    if (!a || a.target === '_blank' || a.hasAttribute('download') || a.closest('[data-no-loader]')) return;
    const url = new URL(a.href, location.href);
    if (url.origin !== location.origin) return;
    if (url.pathname === location.pathname && url.search === location.search) return;
    if (/^(mailto|tel|javascript):/i.test(a.getAttribute('href') || '')) return;
    clearTimeout(showTimer);
    showTimer = setTimeout(start, 300);
  });
  window.addEventListener('pageshow', stop);
  window.addEventListener('pagehide', () => clearTimeout(showTimer));
})();

/* Bevestiging op de knop zelf: direct na "Doos van 6" een vinkje en "In je mand", los van de mini-winkelmand.
   Mislukt het toevoegen, dan gaat de knop meteen terug. */
(() => {
  const CHECK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path class="vn-added-mark__tick" d="M20 6 9 17l-5-5"/></svg>';
  const timers = new WeakMap();

  const reset = (btn) => {
    btn.classList.remove('is-vn-added');
    btn.querySelector('.vn-added-mark')?.remove();
  };

  const mark = (btn) => {
    clearTimeout(timers.get(btn));
    reset(btn);
    const m = document.createElement('span');
    m.className = 'vn-added-mark';
    m.innerHTML = `${CHECK}<span>In je mand</span>`;
    btn.append(m);
    void btn.offsetWidth;
    btn.classList.add('is-vn-added');
    timers.set(btn, setTimeout(() => reset(btn), 2200));
  };

  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (event.vnMini || event.action !== 'add' || !(event.target instanceof Element)) return;
    const form = event.target.closest('product-form-component');
    const btn = form?.querySelector('[ref="addToCartButton"], button[name="add"]');
    if (!btn) return;
    mark(btn);
    (event.promise || Promise.resolve())
      .then((res) => { if (res?.detail?.didError) { clearTimeout(timers.get(btn)); reset(btn); } })
      .catch(() => { clearTimeout(timers.get(btn)); reset(btn); });
  });
})();

/* Fotowissel in kaarten (vn-card-grid, data-vn-slides): om de 4,5 seconde de volgende foto, alleen in beeld,
   pauze bij muis of focus. Stippen en pijlen om zelf te kiezen. Bij minder beweging geen automatische wissel. */
(() => {
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const arrow = (d) => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${d}"/></svg>`;

  const setup = (root) => {
    if (root.dataset.vnSlidesReady) return;
    const items = [...root.querySelectorAll('.vn-slides__item')];
    if (items.length < 2) return;
    root.dataset.vnSlidesReady = '1';
    let i = 0, timer = 0, visible = false, paused = false;

    const dots = document.createElement('div');
    dots.className = 'vn-slides__dots';
    items.forEach((_, n) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'vn-slides__dot';
      b.setAttribute('aria-label', `Foto ${n + 1} van ${items.length}`);
      b.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); go(n); });
      dots.append(b);
    });
    const nav = (cls, label, d, step) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `vn-slides__nav vn-slides__nav--${cls}`;
      b.setAttribute('aria-label', label);
      b.innerHTML = arrow(d);
      b.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); go(i + step); });
      return b;
    };
    root.append(nav('prev', 'Vorige foto', 'M15 18l-6-6 6-6', -1), nav('next', 'Volgende foto', 'M9 18l6-6-6-6', 1), dots);

    const go = (n) => {
      i = (n + items.length) % items.length;
      items.forEach((el, k) => {
        el.classList.toggle('is-on', k === i);
        el.setAttribute('aria-hidden', k === i ? 'false' : 'true');
      });
      [...dots.children].forEach((d, k) => d.setAttribute('aria-current', k === i ? 'true' : 'false'));
      tick();
    };
    const tick = () => {
      clearTimeout(timer);
      if (visible && !paused && !still.matches) timer = setTimeout(() => go(i + 1), 4500);
    };
    root.addEventListener('pointerenter', () => { paused = true; tick(); });
    root.addEventListener('pointerleave', () => { paused = false; tick(); });
    root.addEventListener('focusin', () => { paused = true; tick(); });
    root.addEventListener('focusout', () => { paused = false; tick(); });
    new IntersectionObserver(([e]) => { visible = e.isIntersecting; tick(); }, { threshold: 0.4 }).observe(root);
    go(0);
  };

  const init = () => document.querySelectorAll('[data-vn-slides]').forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();

/* Collage (vn-collage): video's spelen zonder geluid in een lus zolang ze in beeld zijn.
   Bij minder beweging of databesparing blijven ze stil op het eerste beeld. */
(() => {
  const saveData = !!(navigator.connection && navigator.connection.saveData);
  const still = saveData ? { matches: true } : window.matchMedia('(prefers-reduced-motion: reduce)');
  const io = new IntersectionObserver((entries) => {
    entries.forEach(({ target: v, isIntersecting }) => {
      if (isIntersecting && !still.matches && v.offsetParent !== null) {
        v.muted = true;
        v.play().catch(() => {});
      } else {
        v.pause();
      }
    });
  }, { threshold: 0.25 });
  const init = () => document.querySelectorAll('[data-vn-collage] video').forEach((v) => {
    if (v.dataset.vnWatched) return;
    v.dataset.vnWatched = '1';
    v.muted = true;
    v.setAttribute('playsinline', '');
    io.observe(v);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();

/* Mascotte in de kop (vn-hero, data-vn-mascot): de wijn in zijn glas loopt vol met een golfje,
   daarna verschijnt het briefje. Om de 9 seconden walst de wijn even rond. Alleen in beeld;
   bij minder beweging meteen een vol glas. Coördinaten horen bij vn-mascotte-wijn.webp (800 x 769). */
(() => {
  const EMPTY = 214, FULL = 64, X0 = 104, X1 = 272;
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  const setup = (root) => {
    if (root.dataset.vnReady) return;
    root.dataset.vnReady = '1';
    const level = root.querySelector('.vn-mascot__level');
    if (!level) return;
    const draw = (y, amp, phase) => {
      let d = `M0 769H800V${y.toFixed(1)}H${X1}`;
      for (let x = X1; x >= X0; x -= 6) d += `L${x} ${(y + amp * Math.sin(x / 18 + phase)).toFixed(1)}`;
      level.setAttribute('d', `${d}H0Z`);
    };
    const full = () => { draw(FULL, 0, 0); root.classList.add('is-full'); };
    if (still.matches) return full();

    let raf = 0, visible = false, started = false, timer = 0;
    const run = (from, to, dur, amp0, done) => {
      const t0 = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - t0) / dur);
        const y = from + (to - from) * ease(t);
        draw(y, amp0 * (1 - t) + 1.5 * Math.sin(t * Math.PI), now / 160);
        if (t < 1) raf = requestAnimationFrame(step);
        else { draw(to, 0, 0); done && done(); }
      };
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(step);
    };
    /* Walsen: het niveau zakt even aan één kant en komt golvend terug */
    const swirl = () => {
      if (!visible) return;
      const t0 = performance.now(), dur = 1800;
      const step = (now) => {
        const t = Math.min(1, (now - t0) / dur);
        const amp = 9 * Math.sin(t * Math.PI) * (1 - t * 0.4);
        draw(FULL + 10 * Math.sin(t * Math.PI), amp, now / 120);
        if (t < 1) raf = requestAnimationFrame(step); else draw(FULL, 0, 0);
      };
      raf = requestAnimationFrame(step);
    };
    const loop = () => { clearInterval(timer); timer = setInterval(swirl, 9000); };

    draw(EMPTY + 4, 0, 0);
    new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
      if (visible && !started) {
        started = true;
        setTimeout(() => run(EMPTY + 4, FULL, 3400, 7, () => { root.classList.add('is-full'); loop(); }), 500);
      }
    }, { threshold: 0.3 }).observe(root);
  };

  const init = () => document.querySelectorAll('[data-vn-mascot]').forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();

/* Aanvraag vanuit een kaart (bijvoorbeeld een wijnpakket): de link naar #contact vult het bericht alvast in
   met de naam van de kaart, zodat je direct verder kunt typen. */
(() => {
  document.addEventListener('click', (e) => {
    const a = e.target instanceof Element ? e.target.closest('a[href="#contact"]') : null;
    const card = a?.closest('.vn-cgrid__card');
    if (!card) return;
    const title = card.querySelector('.vn-cgrid__title')?.textContent.trim();
    const box = document.querySelector('#contact textarea[name="contact[body]"]');
    if (!title || !box || box.value.trim()) return;
    box.value = `Ik heb interesse in het pakket "${title}". `;
    setTimeout(() => { try { box.focus({ preventScroll: true }); box.setSelectionRange(box.value.length, box.value.length); } catch (err) {} }, 600);
  });
})();
